// ============================================
// COMPANIES.JSX - All Companies List Page
// ============================================
// This page displays a grid of all companies in the admin panel
// Users can search companies and click to view detailed company information

import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAllCompaniesQuery,
  useDeleteCompanyMutation,
  useSuspendCompanyMutation,
  useUnsuspendCompanyMutation,
  useCreateCompanyWithOwnerMutation,
} from "../../../services/api";
import "./Companies.css";

// ============================================
// SVG ICON COMPONENTS
// ============================================

/* Search icon - Used in the search input */
const IconSearch = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* Arrow icon - Used in the "Open Company" button */
const IconArrow = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* Trash icon - Used for delete button */
const IconTrash = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

/* Plus icon - Used for the "Create Company" button */
const IconPlus = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const EMPTY_CREATE_FORM = {
  name: "",
  slug: "",
  description: "",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
};

// ============================================
// MAIN COMPONENT
// ============================================
export default function AdminCompanies() {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, company: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [createResult, setCreateResult] = useState(null); // success panel data
  const [createError, setCreateError] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Mutations
  const [deleteCompany, { isLoading: isDeleting }] = useDeleteCompanyMutation();
  const [suspendCompany] = useSuspendCompanyMutation();
  const [unsuspendCompany] = useUnsuspendCompanyMutation();
  const [createCompany, { isLoading: isCreating }] = useCreateCompanyWithOwnerMutation();

  // ============================================
  // DEBOUNCE SEARCH QUERY
  // ============================================
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // ============================================
  // FETCH COMPANIES FROM API
  // ============================================
  const {
    data: companiesData,
    isLoading,
    error
  } = useGetAllCompaniesQuery({
    page: 1,
    limit: 50,
    search: debouncedQuery || undefined,
  });

  const companies = companiesData?.companies || [];

  // ============================================
  // KEYBOARD SHORTCUT - Ctrl/Cmd + K
  // ============================================
  useEffect(() => {
    const onKey = (e) => {
      const isCtrlK = (e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "k");
      if (isCtrlK) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ============================================
  // TOGGLE ACTIVE/INACTIVE HANDLER
  // ============================================
  const handleToggleActive = async (e, company) => {
    e.stopPropagation();
    try {
      if (company.isActive) {
        await suspendCompany({ id: company._id, reason: "Deactivated by admin" }).unwrap();
      } else {
        await unsuspendCompany(company._id).unwrap();
      }
    } catch (err) {
      console.error("Failed to toggle company status:", err);
    }
  };

  // ============================================
  // CREATE COMPANY HANDLERS
  // ============================================
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const closeCreateModal = () => {
    setCreateOpen(false);
    setForm(EMPTY_CREATE_FORM);
    setCreateResult(null);
    setCreateError("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError("");
    try {
      const res = await createCompany({
        name: form.name.trim(),
        slug: form.slug.trim() || undefined, // blank => backend auto-generates from name
        description: form.description.trim() || undefined,
        owner: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          password: form.password,
        },
      }).unwrap();
      // Keep the modal open to show the credentials the admin must hand off.
      setCreateResult(res);
    } catch (err) {
      setCreateError(err?.data?.message || "Failed to create company. Please try again.");
    }
  };

  // ============================================
  // DELETE HANDLER
  // ============================================
  const handleDelete = async () => {
    if (!deleteModal.company) return;

    try {
      await deleteCompany(deleteModal.company._id).unwrap();
      setDeleteModal({ isOpen: false, company: null });
    } catch (err) {
      console.error("Failed to delete company:", err);
    }
  };

  // ============================================
  // RENDER JSX
  // ============================================
  return (
    <div className="acomps">
      {/* ========== HEADER SECTION ========== */}
      <div className="acomps-top">
        <div className="welcome">← Back Home</div>
        <h1 className="username">All Companies</h1>
      </div>

      {/* ========== FILTER BAR ========== */}
      <div className="acomps-filterbar">
        <div className="acomps-count">
          Showing All Companies – <strong>{String(companies.length).padStart(2, "0")}</strong>
        </div>

        <div className="acomps-search">
          <IconSearch />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="key-hint">Ctrl K</span>
        </div>

        <button className="btn" onClick={() => setCreateOpen(true)}>
          <IconPlus /> Create Company
        </button>
      </div>

      {/* ========== LOADING STATE ========== */}
      {isLoading && (
        <div className="acomps-loading">
          Loading companies...
        </div>
      )}

      {/* ========== ERROR STATE ========== */}
      {error && (
        <div className="acomps-error">
          Failed to load companies. Please try again.
        </div>
      )}

      {/* ========== EMPTY STATE ========== */}
      {!isLoading && !error && companies.length === 0 && (
        <div className="acomps-empty">
          No companies found.
        </div>
      )}

      {/* ========== COMPANY CARDS GRID ========== */}
      {!isLoading && !error && companies.length > 0 && (
        <div className="acomps-grid">
          {companies.map((company) => (
            <div className="acomps-card" key={company._id}>
              <div className="acomps-card-head">
                <div className="acomps-logo-placeholder">
                  {company.name.charAt(0).toUpperCase()}
                </div>
                <div className="acomps-meta">
                  <div className="acomps-name">{company.name}</div>
                  <div className="acomps-sub">
                    {company.memberCount || 0} members
                    <label
                      className="acomps-toggle"
                      onClick={(e) => e.stopPropagation()}
                      title={company.isActive ? "Click to deactivate" : "Click to activate"}
                    >
                      <input
                        type="checkbox"
                        checked={!!company.isActive}
                        onChange={(e) => handleToggleActive(e, company)}
                      />
                      <span className="acomps-toggle-slider" />
                      <span className={`acomps-toggle-label ${company.isActive ? "active" : "inactive"}`}>
                        {company.isActive ? "Active" : "Inactive"}
                      </span>
                    </label>
                  </div>
                </div>
                <button
                  className="acomps-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteModal({ isOpen: true, company });
                  }}
                  title="Delete company"
                >
                  <IconTrash />
                </button>
              </div>

              <div className="acomps-card-info">
                {company.ownerId && (
                  <div className="acomps-owner">
                    Owner: {company.ownerId.firstName} {company.ownerId.lastName}
                  </div>
                )}
                {company.inviteCode && (
                  <div className="acomps-invite">
                    Invite Code: <code>{company.inviteCode}</code>
                  </div>
                )}
              </div>

              <button
                className="btn full"
                onClick={() => navigate(`/admin/companies/${company._id}`)}
              >
                Open Company <IconArrow />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ========== DELETE CONFIRMATION MODAL ========== */}
      {deleteModal.isOpen && (
        <div className="acomps-modal-overlay" onClick={() => setDeleteModal({ isOpen: false, company: null })}>
          <div className="acomps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="acomps-modal-header">
              <h3>Delete Company</h3>
            </div>
            <div className="acomps-modal-body">
              <p>
                Are you sure you want to delete <strong>{deleteModal.company?.name}</strong>?
              </p>
              <p className="acomps-modal-warning">
                This action cannot be undone. All {deleteModal.company?.memberCount || 0} members will be orphaned and can join another company using an invite code.
              </p>
            </div>
            <div className="acomps-modal-actions">
              <button
                className="btn secondary"
                onClick={() => setDeleteModal({ isOpen: false, company: null })}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Company"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CREATE COMPANY MODAL ========== */}
      {createOpen && (
        <div className="acomps-modal-overlay" onClick={closeCreateModal}>
          <div className="acomps-modal acomps-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="acomps-modal-header">
              <h3>{createResult ? "Company Created" : "Create Company"}</h3>
            </div>

            {/* ----- SUCCESS PANEL: credentials to hand off ----- */}
            {createResult ? (
              <>
                <div className="acomps-modal-body">
                  <p>
                    <strong>{createResult.company?.name}</strong> was created and is active.
                    Share these credentials with the owner — they can log in right away.
                  </p>
                  <div className="acomps-cred">
                    <span className="acomps-cred-label">Owner email</span>
                    <code>{createResult.ownerEmail}</code>
                  </div>
                  <div className="acomps-cred">
                    <span className="acomps-cred-label">Temporary password</span>
                    <code>{createResult.tempPassword}</code>
                  </div>
                  <div className="acomps-cred">
                    <span className="acomps-cred-label">Invite code</span>
                    <code>{createResult.inviteCode}</code>
                  </div>
                  <p className="acomps-modal-warning">
                    The temporary password is shown only once here. Copy it before closing.
                  </p>
                </div>
                <div className="acomps-modal-actions">
                  <button
                    className="btn secondary"
                    onClick={() =>
                      navigator.clipboard?.writeText(
                        `Company: ${createResult.company?.name}\nEmail: ${createResult.ownerEmail}\nTemporary password: ${createResult.tempPassword}\nInvite code: ${createResult.inviteCode}`
                      )
                    }
                  >
                    Copy details
                  </button>
                  <button className="btn" onClick={closeCreateModal}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              /* ----- CREATE FORM ----- */
              <form onSubmit={handleCreate}>
                <div className="acomps-modal-body">
                  {createError && <div className="acomps-error">{createError}</div>}

                  <h4 className="acomps-form-section">Company</h4>
                  <div className="acomps-field">
                    <label>Company name *</label>
                    <input type="text" value={form.name} onChange={setField("name")} required maxLength={100} />
                  </div>
                  <div className="acomps-field">
                    <label>Slug</label>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={setField("slug")}
                      placeholder="auto-generated from name if blank"
                      pattern="[a-z0-9-]+"
                      title="Lowercase letters, numbers and hyphens only"
                    />
                  </div>
                  <div className="acomps-field">
                    <label>Description</label>
                    <textarea value={form.description} onChange={setField("description")} rows={2} maxLength={500} />
                  </div>

                  <h4 className="acomps-form-section">Owner account</h4>
                  <div className="acomps-form-grid">
                    <div className="acomps-field">
                      <label>First name *</label>
                      <input type="text" value={form.firstName} onChange={setField("firstName")} required maxLength={50} />
                    </div>
                    <div className="acomps-field">
                      <label>Last name *</label>
                      <input type="text" value={form.lastName} onChange={setField("lastName")} required maxLength={50} />
                    </div>
                  </div>
                  <div className="acomps-field">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={setField("email")} required />
                  </div>
                  <div className="acomps-field">
                    <label>Temporary password *</label>
                    <input type="text" value={form.password} onChange={setField("password")} required minLength={8} />
                    <small className="acomps-hint">Min 8 characters. The owner can change it after logging in.</small>
                  </div>
                </div>
                <div className="acomps-modal-actions">
                  <button type="button" className="btn secondary" onClick={closeCreateModal} disabled={isCreating}>
                    Cancel
                  </button>
                  <button type="submit" className="btn" disabled={isCreating}>
                    {isCreating ? "Creating..." : "Create Company"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
